<script setup lang="ts">
import { ECardLinkSize, ESeoHeadingSize, type ICardLinkProps } from '../ts/types'
import { scrollTo } from '../../../utils/scrollTo'
import { useImage } from '../../../composables/useImageUrl'
import { getLinkTarget } from '../../../utils/linkTarget'
import type { ICTABlockAction } from '../../CTABlock/ts/types'
import { NuxtLink } from '#components'
import { isDark } from '@/ui/helpers/utils'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'
import { EClickElement } from '@/cms-ui/utils/dataLayer'

const props = withDefaults(defineProps<ICardLinkProps>(), {
    seoHeadingSize: ESeoHeadingSize.H2,
})

const { image: responsiveImage } = useImage(props.image, props.page)

const { openDrawer } = useDrawer()

function handleClick(action: ICTABlockAction) {
    if (action.drawer) {
        openDrawer(action.drawer, props.page)
    }
    if (action.anchor) {
        scrollTo(action?.anchor)
    }
}
</script>

<template>
    <component
        :is="action?.url ? 'a' : 'button'"
        :href="action?.url"
        :target="action ? getLinkTarget(action) : undefined"
        class="p-sm rounded-container-lg bg-neutral-secondary hover:bg-neutral-tertiary active:bg-neutral-tertiary inline-flex cursor-pointer items-center justify-between transition-colors duration-200"
        :class="[
            isDark(dark),
            { 'basis-[calc(50%-0.25rem)] md:basis-[calc(50%-0.5rem)]': mobileSize === ECardLinkSize.Small },
            { 'basis-full md:basis-[calc(50%-0.5rem)]': mobileSize === ECardLinkSize.Large },
            { 'lg:basis-[calc(25%-1.125rem)]': desktopSize === ECardLinkSize.Small },
            { 'lg:basis-[calc(33.3%-1rem)]': desktopSize === ECardLinkSize.Large },
        ]"
        :data-test="dataTest"
        @click="[
            useClickTracking({
                label: props.title,
                clickPath: props.action?.url || '',
                clickElement: EClickElement.BUTTON,
                componentName: props.title || 'LinkCard',
                componentCategory: 'LinkCard',
            }),
            handleClick(action),
        ]"
    >
        <div class="flex items-center text-left">
            <BaseImage
                v-if="image"
                class=""
                :class="[
                    { 'max-lg:hidden': desktopSize === ECardLinkSize.Small && mobileSize !== ECardLinkSize.Large },
                    { 'max-lg:hidden': mobileImageOff },
                    { 'lg:hidden': desktopImageOff },
                ]"
                picture-classes="rounded-container-md size-[56px] min-w-[56px] mr-sm"
                :image-data="responsiveImage"
                :alt="props.image?.altText"
            />
            <component
                :is="seoHeadingSize"
                class="label-lg text-comp-high-contrast line-clamp-2"
                :class="isDark(dark)"
            >
                {{ title }}
            </component>
        </div>
        <VIcon
            class="ml-sm text-brand-accent-invert"
            :dark="dark"
            name="arrow-right"
            size="m"
        />
    </component>
</template>
